/**
 * Derive Bugsnap context (Feature Group + Workflow/Agent ID) from a URL.
 *
 * Used by both the manual modal and the auto reporter so the two flows stay in
 * sync. Feature Group is resolved against the left-nav definitions so the label
 * matches what the user sees in the sidebar.
 */

import i18n from '../../i18n/config'
import { NAV_SECTIONS } from '../../constants/navigation'

const ALL_NAV_ITEMS = NAV_SECTIONS.flatMap((s) => s.items)

// Routes that are reachable but not present in the left-nav (e.g. Help and
// Settings sub-pages). Bugsnap still needs a Feature Group label for them, so we
// resolve these by longest-prefix match after the nav lookup fails. Most
// specific path first is not required — the longest match wins.
const STATIC_FEATURE_GROUPS: ReadonlyArray<{ path: string; label: string }> = [
  { path: '/help/bug-reports', label: 'Bug Reports' },
  { path: '/help', label: 'Help' },
  { path: '/settings', label: 'Settings' },
]

const UUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi

function toPathname(urlOrPath: string): string {
  if (!urlOrPath) return ''
  try {
    // Absolute URL — extract the pathname.
    return new URL(urlOrPath).pathname
  } catch {
    // Already a path (e.g. "/agent-studio/canvas/..").
    return urlOrPath.split('?')[0].split('#')[0]
  }
}

/**
 * Map a URL/path to its left-nav feature label (e.g. "/agent-studio/canvas/x"
 * -> "Agent Studio", "/admin" -> "Admin"). Uses longest-prefix match so nested
 * routes like "/ontology/migrations" resolve to the most specific item.
 * Returns undefined when no nav item matches.
 */
export function deriveFeatureGroup(urlOrPath: string): string | undefined {
  const pathname = toPathname(urlOrPath)
  if (!pathname) return undefined

  let best: { path: string; labelKey: string } | undefined
  for (const item of ALL_NAV_ITEMS) {
    const p = item.path
    if (p === '/') {
      if (pathname === '/' && !best) best = { path: p, labelKey: item.labelKey }
      continue
    }
    if (pathname === p || pathname.startsWith(`${p}/`)) {
      if (!best || p.length > best.path.length) best = { path: p, labelKey: item.labelKey }
    }
  }
  if (!best) {
    // No left-nav match — fall back to known non-nav routes (Help/Settings).
    let staticBest: { path: string; label: string } | undefined
    for (const entry of STATIC_FEATURE_GROUPS) {
      if (pathname === entry.path || pathname.startsWith(`${entry.path}/`)) {
        if (!staticBest || entry.path.length > staticBest.path.length) staticBest = entry
      }
    }
    return staticBest?.label
  }
  // Fall back to the raw key if a translation is missing.
  const label = i18n.t(best.labelKey)
  return label && label !== best.labelKey ? label : undefined
}

/**
 * Extract the workflow/agent (entity) ID from a URL — the last UUID present in
 * the path (e.g. "/agent-studio/canvas/<uuid>"). Returns undefined when the URL
 * carries no entity ID (e.g. "/admin").
 */
export function deriveWorkflowId(urlOrPath: string): string | undefined {
  const pathname = toPathname(urlOrPath)
  const matches = pathname.match(UUID_RE)
  return matches && matches.length > 0 ? matches[matches.length - 1] : undefined
}

export function deriveContext(urlOrPath: string): {
  featureGroup?: string
  workflowId?: string
} {
  return {
    featureGroup: deriveFeatureGroup(urlOrPath),
    workflowId: deriveWorkflowId(urlOrPath),
  }
}
